import { faDiamond } from "@fortawesome/pro-regular-svg-icons";
import { capitalize } from "lodash";
import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import {
  InputSchemaTypes,
  OutputSchemaTypes,
  SchemaTypes,
} from "src/api/flowTypes";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { EventConfigEnriched } from "src/clients/features-control";
import { getSchemaIcon } from "src/entities/entityView/utils";
import { EntitySchemaResource, useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { getEventIcon } from "src/eventsCatalogue/utils";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { isInternalSchemaDefKey } from "src/jsonSchema/validateJsonSchema";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  entityNameToJsonRefPath,
  eventNameToJsonRefPath,
  extractFieldNameFromJsonRefPath,
  isJsonRefPath,
} from "src/schema/jsonRefUtils";
import {
  SchemaTypeSelectorProps,
  SchemaUI,
} from "src/schema/schemaMappingUtils";

const INPUT_SCHEMA_TYPES_OPTIONS_BASE = Object.entries(InputSchemaTypes).map(
  ([key, value]) => ({
    key: key,
    value: value,
    icon: SCHEMA_TYPE_ICONS[value],
  }),
);

export const getEntitySchemaOptions = (
  entitySchemas: EntitySchemaResource[],
) => {
  return entitySchemas?.map((entity) => ({
    key: entityNameToJsonRefPath(entity._id),
    value: capitalize(entity._display_name_singular),
    icon: getSchemaIcon(entity),
    schemaName: entity._id,
  }));
};

export const getEventSchemaOptions = (eventSchemas: EventConfigEnriched[]) =>
  eventSchemas?.map((eventSchema) => ({
    key: eventNameToJsonRefPath(eventSchema.event_type),
    value: capitalize(eventSchema.display_name_singular),
    icon: getEventIcon(eventSchema),
    internalName: eventSchema.internal_name,
  }));

const OUTPUT_SCHEMA_TYPES_OPTIONS_BASE = Object.entries(OutputSchemaTypes).map(
  ([key, value]) => ({
    key: key,
    value: value,
    icon: SCHEMA_TYPE_ICONS[value],
  }),
);

export const SchemaTypeSelector: React.FC<SchemaTypeSelectorProps> = ({
  rowName,
  disabled,
  index,
  type,
}) => {
  const { workspace } = useWorkspaceContext();
  const featureGates = useWorkspaceFeatureGates();
  const { setValue, getValues } = useFormContext<SchemaUI>();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
    options: {
      enabled: featureGates.entitiesEnabled,
    },
  });
  const { data: eventSchemas } = useEventSchemas();

  const fieldTypeOptions = useMemo(() => {
    const baseOptions =
      type === "input"
        ? INPUT_SCHEMA_TYPES_OPTIONS_BASE
        : OUTPUT_SCHEMA_TYPES_OPTIONS_BASE;

    const entitySchemaOptions =
      featureGates.entitiesEnabled && entitySchemas?.entities
        ? getEntitySchemaOptions(entitySchemas?.entities)
        : [];

    const eventSchemaOptions =
      featureGates.featuresEventsEnabled && eventSchemas
        ? getEventSchemaOptions(eventSchemas)
        : [];

    const fieldType = getValues(`properties.${index}.type.0`);
    const isJsonRef = isJsonRefPath(fieldType);
    const customOption =
      (fieldType === "union" || isJsonRef) && !isInternalSchemaDefKey(fieldType)
        ? [
            {
              key: fieldType,
              value: isJsonRef
                ? extractFieldNameFromJsonRefPath(fieldType)
                : "Custom",
              icon: faDiamond,
              disabled: true,
            },
          ]
        : [];

    return [
      ...baseOptions,
      ...entitySchemaOptions,
      ...eventSchemaOptions,
      ...customOption,
    ];
  }, [
    type,
    entitySchemas?.entities,
    eventSchemas,
    featureGates,
    getValues,
    index,
  ]);

  return (
    <FixedSimpleDropdown
      buttonDataLoc="schema-field-type"
      dataLoc={`schema-field-type-${index}`}
      disabled={disabled}
      elements={fieldTypeOptions}
      placeholder="Select type"
      placement="bottom start"
      selectedKey={getValues(`${rowName}.type.0`)}
      onSelect={(newType) => {
        const value = getValues(`${rowName}.type.0`);
        if (newType === "enum") {
          if (value !== "enum") {
            setValue(`properties.${index}.enum`, [{ value: '"value_1"' }]);
          }
        } else {
          setValue(`properties.${index}.enum`, undefined);
        }
        setValue(`${rowName}.type.0`, newType as SchemaTypes);
      }}
    />
  );
};
